import { Component, type ErrorInfo, type ReactNode } from 'react';

// Error boundary del framework de rollout: envuelve la variante TREATMENT gateada (menu-v2)
// para que un error de render/runtime NO tumbe la app y, a la vez, se INSTRUMENTE como
// behavior='error' ligado al experimento (fuente del guardrail max_error_rate_pct — Tier 3).
//
// Propiedades de diseño:
//   - No-bloqueante: al capturar, renderiza `fallback` (típicamente la variante V1/control) en
//     vez de la pantalla rota. El comerciante sigue trabajando.
//   - Fire-and-forget: `onError` (el emit de telemetría) se invoca dentro de componentDidCatch
//     envuelto en try/catch — un fallo del emit jamás vuelve a romper el árbol.
//   - Re-render por `resetKey`: si cambia (p.ej. el comercio activo o la ruta), el boundary se
//     re-arma e intenta montar la variante otra vez (un error previo no la condena para siempre).

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  // Se renderiza cuando se captura un error (en vez de la pantalla rota). Suele ser V1/control.
  fallback: ReactNode;
  // Callback fire-and-forget para emitir telemetría. NUNCA debe lanzar (lo protegemos igual).
  onError: (error: unknown, info?: ErrorInfo) => void;
  // Si cambia, el boundary se resetea (vuelve a intentar montar children). Opcional.
  resetKey?: string | number;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
}

class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): FeatureErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: FeatureErrorBoundaryProps): void {
    // Reset al cambiar resetKey: re-armamos el boundary para reintentar montar la variante.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    try {
      this.props.onError(error, info);
    } catch {
      // El emit de telemetría es best-effort: su fallo no debe re-romper el árbol.
    }
  }

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

export default FeatureErrorBoundary;
